import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { X, Eye, CheckCircle2, Zap } from "lucide-react";
import { toast } from "sonner";
import SimulateModal from "@/components/SimulateModal";

const STATUS_BADGES = {
  pending: "bg-amber-500/10 border-amber-500/30 text-amber-300",
  paid: "bg-cp-primary/10 border-cp-primary/30 text-cp-primary-light",
  expired: "bg-cp-elevated border-white/10 text-slate-400",
};

const STATUS_LABEL = { pending: "Aguardando", paid: "Pago", expired: "Expirado" };

export default function Transactions() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simOpen, setSimOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/transactions", { params: filter ? { status: filter } : {} });
    setList(data);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      void load();
    }, 8000);
    return () => clearInterval(t);
  }, [load]);

  const openDetails = async (id) => {
    const { data } = await api.get(`/transactions/${id}`);
    setSelected(data);
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/simulate/pix-paid/${id}`);
      toast.success("Marcado como pago");
      load();
      if (selected?.transaction?.id === id) openDetails(id);
    } catch (e) {
      toast.error("Falha");
    }
  };

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <SimulateModal open={simOpen} onClose={() => setSimOpen(false)} onCreated={load} />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Transações</h1>
          <p className="text-slate-400 text-sm mt-1">Todas as transações recebidas via webhook ou simulador.</p>
          <p className="text-slate-500 text-xs mt-2 max-w-xl">
            Tentativas ignoradas por falta de telefone não viram linha aqui - aparecem como eventos de webhook em{" "}
            <Link to="/app/integrations?events=missing_phone" className="text-cp-primary-light underline font-mono">
              Integrações → Sem telefone
            </Link>
            .
          </p>
        </div>
        <button
          onClick={() => setSimOpen(true)}
          data-testid="tx-simulate-btn"
          className="bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 text-amber-300 font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 text-sm"
        >
          <Zap className="h-4 w-4" /> Simular PIX
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { k: "", l: "Todas" },
          { k: "pending", l: "Aguardando" },
          { k: "paid", l: "Pagos" },
          { k: "expired", l: "Expirados" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`filter-${f.k || "all"}`}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border rounded-sm transition ${
              filter === f.k ? "bg-cp-primary text-cp-bg border-cp-primary font-bold" : "border-white/10 text-slate-400 hover:text-white hover:border-white/10"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="border border-white/[0.06] bg-cp-bg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cp-elevated/50 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              <tr>
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-left px-4 py-3">Telefone</th>
                <th className="text-right px-4 py-3">Valor</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Recuperado</th>
                <th className="text-left px-4 py-3">Criado</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500 font-mono text-xs">carregando…</td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-slate-500">
                    <div className="font-heading text-lg">Nenhuma transação ainda</div>
                    <div className="text-xs mt-1">Use o Simulador para testar ou configure o webhook do seu checkout.</div>
                  </td>
                </tr>
              ) : (
                list.map((tx) => (
                  <tr key={tx.id} className="border-t border-white/[0.06] hover:bg-cp-elevated/30" data-testid={`tx-row-${tx.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{tx.customer_name}</div>
                      <div className="text-xs text-slate-500 font-mono flex flex-wrap items-center gap-1">
                        <span>{tx.external_id}</span>
                        {typeof tx.external_id === "string" && tx.external_id.startsWith("cpix_") ? (
                          <span
                            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-cyan-500/30 text-cyan-300/90"
                            title="Chave externa derivada do cpix_id (gateway não enviou order id)"
                          >
                            cpix
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-300">{tx.customer_phone}</td>
                    <td className="px-4 py-3 text-right font-heading font-bold tabular-nums">{formatCurrency(tx.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border ${STATUS_BADGES[tx.status] || ""}`}>
                        {STATUS_LABEL[tx.status] || tx.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {tx.recovered ? <span className="text-cp-primary-light text-xs font-mono">✓ RECUPERADO</span> : <span className="text-slate-600 text-xs">-</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{formatDateTime(tx.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => openDetails(tx.id)}
                          data-testid={`view-tx-${tx.id}`}
                          className="p-1.5 border border-white/10 hover:bg-cp-elevated rounded-sm"
                          title="Ver logs"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        {tx.status === "pending" && (
                          <button
                            onClick={() => markPaid(tx.id)}
                            data-testid={`pay-tx-${tx.id}`}
                            className="p-1.5 border border-cp-primary/30 bg-cp-primary/10 hover:bg-cp-primary/20 text-cp-primary-light rounded-sm"
                            title="Marcar pago (demo)"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide-over details */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="tx-details-drawer">
          <div className="flex-1 bg-black/60" onClick={() => setSelected(null)} />
          <div className="w-full max-w-lg bg-cp-bg border-l border-white/[0.06] overflow-y-auto">
            <div className="p-6 border-b border-white/[0.06] flex items-start justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-cp-primary-light">_detalhes</div>
                <div className="font-heading text-xl font-bold mt-1">{selected.transaction.customer_name}</div>
                <div className="text-xs text-slate-500 font-mono mt-1 flex flex-wrap items-center gap-1">
                  <span>{selected.transaction.external_id}</span>
                  {typeof selected.transaction.external_id === "string" &&
                  selected.transaction.external_id.startsWith("cpix_") ? (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-cyan-500/30 text-cyan-300/90">cpix</span>
                  ) : null}
                </div>
              </div>
              <button onClick={() => setSelected(null)} data-testid="close-drawer" className="text-slate-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="border border-white/[0.06] bg-cp-elevated/30 p-3 rounded-sm">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">valor</div>
                  <div className="font-heading text-lg font-bold">{formatCurrency(selected.transaction.amount)}</div>
                </div>
                <div className="border border-white/[0.06] bg-cp-elevated/30 p-3 rounded-sm">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">status</div>
                  <div className="font-heading text-lg font-bold">{STATUS_LABEL[selected.transaction.status]}</div>
                </div>
                <div className="border border-white/[0.06] bg-cp-elevated/30 p-3 rounded-sm">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">telefone</div>
                  <div className="font-mono text-sm">{selected.transaction.customer_phone}</div>
                </div>
                <div className="border border-white/[0.06] bg-cp-elevated/30 p-3 rounded-sm">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">recuperado</div>
                  <div className="font-heading text-sm">{selected.transaction.recovered ? "sim ✓" : "não"}</div>
                </div>
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">jobs agendados</div>
                {selected.jobs.length === 0 ? (
                  <div className="text-xs text-slate-500">Nenhum job</div>
                ) : (
                  <ul className="space-y-1.5">
                    {selected.jobs.map((j) => (
                      <li key={j.id} className="text-xs font-mono border border-white/[0.06] px-3 py-2 rounded-sm flex justify-between">
                        <span className="text-slate-400">passo {j.step_index} · {j.status}</span>
                        <span className="text-slate-500">{formatDateTime(j.run_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">mensagens enviadas</div>
                {selected.logs.length === 0 ? (
                  <div className="text-xs text-slate-500">Nenhuma mensagem ainda</div>
                ) : (
                  <ul className="space-y-2">
                    {selected.logs.map((l) => (
                      <li key={l.id} className="border border-white/[0.06] p-3 rounded-sm" data-testid={`log-${l.id}`}>
                        <div className="flex justify-between items-center mb-2 text-xs font-mono">
                          <span className="text-slate-400">passo {l.step_index}</span>
                          <span className={l.status === "sent" ? "text-cp-primary-light" : l.status === "failed" ? "text-red-400" : "text-slate-500"}>
                            {l.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-sm text-slate-300 whitespace-pre-wrap">{l.message}</div>
                        {l.error && <div className="text-xs text-red-400 mt-2">{l.error}</div>}
                        <div className="text-[10px] text-slate-600 mt-2 font-mono">{formatDateTime(l.sent_at)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
